/**
 * 档位排序与「某个能力最低要哪一档」的单一真相。
 *
 * 在这之前，付费能力一律是二元的 `isPaidUser()` —— Starter 和 Pro 在分辨率上
 * 毫无区别，Pro 除了积分多没有别的理由存在。竞品（DeeVid）把画质按档位切：
 * 最低档 720P、往上 1080P，这才是订阅阶梯该有的形状。
 *
 * 放在 config 而不是某个 route 下面，是因为前端锁下拉框、后端拒请求、定价页
 * 写文案都要用同一份，分散维护必漂。
 */

export const PLAN_TIERS = ["free", "starter", "pro", "ultimate"] as const;
export type PlanTier = (typeof PLAN_TIERS)[number];

/**
 * 付费订阅档位。任何「是不是付费用户」的判断都从这里取，别再手写
 * `["starter", "pro"]` —— 2026-09-12 加 Ultimate 时就是因为 4 处写死名单没跟着改，
 * Ultimate 订阅者会被当成免费用户（去水印、侧边栏、用户菜单全部失效）。
 */
export const PAID_PLAN_TIERS = ["starter", "pro", "ultimate"] as const;

/** 数字越大权限越高。与 subscription-events.ts 的 planOrder 同序。 */
export const PLAN_RANK: Record<PlanTier, number> = {
	free: 0,
	starter: 1,
	pro: 2,
	ultimate: 3,
};

/**
 * 视频分辨率的最低档位要求。键是 I2V_RESOLUTIONS 的字符串值——这里刻意不 import
 * 那个类型，免得 config 反向依赖 api 包；对不上会在 image-to-video 侧的
 * satisfies 检查里暴露出来。
 *
 * 480p 仍然对免费用户开放：它是「让人看见效果」的那一档，堵死了连撞墙的机会
 * 都没有。720p 从白送改成 Starter 起步——竞品拿它当 $14/月的卖点，我们之前是
 * 免费送的。
 */
export const MIN_PLAN_BY_VIDEO_RESOLUTION: Record<string, PlanTier> = {
	"480p": "free",
	"720p": "starter",
	"1080p": "pro",
	"4K": "pro",
};

/** actual 够不够 required。用排序比较而不是相等，升档自动继承低档权限。 */
export function meetsMinPlan(actual: PlanTier, required: PlanTier): boolean {
	return PLAN_RANK[actual] >= PLAN_RANK[required];
}

/** 该分辨率最低要哪一档；没登记的分辨率当免费档，不误伤新加的档位。 */
export function getMinPlanForVideoResolution(resolution: string): PlanTier {
	return MIN_PLAN_BY_VIDEO_RESOLUTION[resolution] ?? "free";
}

/** 这一档能不能出这个分辨率。前端锁下拉框和后端拒请求共用。 */
export function canUseVideoResolution(
	tier: PlanTier,
	resolution: string,
): boolean {
	return meetsMinPlan(tier, getMinPlanForVideoResolution(resolution));
}
